// src/services/languageService.js
// ─────────────────────────────────────────────────────────────
// طبقة الـ API الخاصة بنظام اللغات الجديد (Language → Category → Skill → Questions)
// مبنية على نفس الـ endpoints الموجودة في languages/urls.py
// ─────────────────────────────────────────────────────────────
import api from "../api/axios";

const BASE = "/languages";

const formDataConfig = { headers: { "Content-Type": "multipart/form-data" } };

/**
 * DRF بيرجّع أخطاء بأشكال مختلفة:
 *  { message } | { error } | { detail } | { field: ["msg"] }
 * الدالة دي بتطلع أول رسالة مفهومة.
 */
export const getApiError = (err, fallback = "حدث خطأ، حاول مرة أخرى") => {
  const data = err?.response?.data;
  if (!data || typeof data === "string") return fallback;
  if (data.message) return data.message;
  if (data.error) return data.error;
  if (data.detail) return data.detail;
  const first = Object.entries(data)[0];
  if (first) {
    const [field, msg] = first;
    return `${field}: ${Array.isArray(msg) ? msg[0] : msg}`;
  }
  return fallback;
};

/* ─────────────── Helper: build FormData when there's a file ─────────────── */
function toFormData(data) {
  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    formData.append(key, value);
  });
  return formData;
}

/* ============================================
   0. LANGUAGE CRUD
   ============================================ */
export const languagesAPI = {
  getAll: async () => {
    const { data } = await api.get(`${BASE}/`);
    return data;
  },
  getOne: async (languageId) => {
    const { data } = await api.get(`${BASE}/${languageId}/`);
    return data;
  },
  create: async (payload) => {
    const hasFile = payload.flag instanceof File;
    const body = hasFile ? toFormData(payload) : payload;
    const { data } = await api.post(`${BASE}/create/`, body, {
      headers: hasFile ? { "Content-Type": "multipart/form-data" } : undefined,
    });
    return data;
  },
  update: async (languageId, payload) => {
    const hasFile = payload.flag instanceof File;
    const body = hasFile ? toFormData(payload) : payload;
    const { data } = await api.patch(`${BASE}/${languageId}/update/`, body, {
      headers: hasFile ? { "Content-Type": "multipart/form-data" } : undefined,
    });
    return data;
  },
  delete: async (languageId) => {
    const { data } = await api.delete(`${BASE}/${languageId}/delete/`);
    return data;
  },
};

/* ============================================
   1. CATEGORY CRUD
   ============================================ */
export const languageCategoriesAPI = {
  getAll: async (languageId) => {
    const { data } = await api.get(`${BASE}/${languageId}/categories/`);
    return data;
  },
  getOne: async (categoryId) => {
    const { data } = await api.get(`${BASE}/categories/${categoryId}/`);
    return data;
  },
  create: async (languageId, payload) => {
    const hasFile = payload.icon instanceof File;
    const body = hasFile ? toFormData(payload) : payload;
    const { data } = await api.post(
      `${BASE}/${languageId}/categories/create/`,
      body,
      { headers: hasFile ? { "Content-Type": "multipart/form-data" } : undefined }
    );
    return data;
  },
  update: async (categoryId, payload) => {
    const hasFile = payload.icon instanceof File;
    const body = hasFile ? toFormData(payload) : payload;
    const { data } = await api.patch(
      `${BASE}/categories/${categoryId}/update/`,
      body,
      { headers: hasFile ? { "Content-Type": "multipart/form-data" } : undefined }
    );
    return data;
  },
  delete: async (categoryId) => {
    const { data } = await api.delete(`${BASE}/categories/${categoryId}/delete/`);
    return data;
  },
};

/* ============================================
   2. SKILL CRUD
   ============================================ */
export const languageSkillsAPI = {
  getAll: async (categoryId) => {
    const { data } = await api.get(`${BASE}/categories/${categoryId}/skills/`);
    return data;
  },
  mergeMixed: async (categoryId, data) =>
  (await api.post(`/languages/categories/${categoryId}/skills/merge-mixed/`, data)).data,
  
  getOne: async (skillId) => {
    const { data } = await api.get(`${BASE}/skills/${skillId}/`);
    return data;
  },
  create: async (categoryId, payload) => {
    const { data } = await api.post(
      `${BASE}/categories/${categoryId}/skills/create/`,
      payload
    );
    return data;
  },
  update: async (skillId, payload) => {
    const { data } = await api.patch(`${BASE}/skills/${skillId}/update/`, payload);
    return data;
  },
  delete: async (skillId) => {
    const { data } = await api.delete(`${BASE}/skills/${skillId}/delete/`);
    return data;
  },
};

/* ============================================
   3. QUESTIONS — CREATE (grouped helper, kept for backward compatibility)
   ============================================ */
export const languageQuestionsAPI = {
  createVocabulary: async (payload) => {
    const { data } = await api.post(`${BASE}/vocabulary/create/`, payload);
    return data;
  },
  createGrammar: async (payload) => {
    const { data } = await api.post(`${BASE}/grammar/create/`, payload);
    return data;
  },
  createReadingPassage: async (payload) => {
    const { data } = await api.post(`${BASE}/reading/passages/create/`, payload);
    return data;
  },
  createReadingQuestion: async (passageId, payload) => {
    const { data } = await api.post(
      `${BASE}/reading/passages/${passageId}/questions/create/`,
      payload
    );
    return data;
  },
  createListeningAudio: async (formData) => {
    const { data } = await api.post(`${BASE}/listening/audio/create/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  createListeningQuestion: async (audioId, payload) => {
    const { data } = await api.post(
      `${BASE}/listening/audio/${audioId}/questions/create/`,
      payload
    );
    return data;
  },
  createSpeakingVideo: async (payload) => {
    const { data } = await api.post(`${BASE}/speaking/videos/create/`, payload);
    return data;
  },
  createSpeakingQuestion: async (videoId, payload) => {
    const { data } = await api.post(
      `${BASE}/speaking/videos/${videoId}/questions/create/`,
      payload
    );
    return data;
  },
  createWriting: async (payload) => {
    const { data } = await api.post(`${BASE}/writing/questions/create/`, payload);
    return data;
  },
  getSkillQuestions: async (skillId, page = 1, pageSize = 50) => {
    const { data } = await api.get(
      `/languages/skills/${skillId}/questions/?page=${page}&page_size=${pageSize}`
    );
    return data;
  },
};

/* ============================================
   4. QUESTIONS — DISPLAY
   ============================================ */
export const getSkillQuestions = async (skillId, page = 1, pageSize = 20) => {
  const { data } = await api.get(`${BASE}/skills/${skillId}/questions/`, {
    params: { page, page_size: pageSize },
  });
  return data;
};

/* ============================================
   Vocabulary Questions (CRUD)
   ============================================ */
export const languageVocabularyAPI = {
  create: async (payload) => {
    const { data } = await api.post(`${BASE}/vocabulary/create/`, payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.patch(`${BASE}/vocabulary/${id}/update/`, payload);
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`${BASE}/vocabulary/${id}/delete/`);
    return data;
  },
};

/* ============================================
   Grammar Questions (CRUD)
   ============================================ */
export const languageGrammarAPI = {
  create: async (payload) => {
    const { data } = await api.post(`${BASE}/grammar/create/`, payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.patch(`${BASE}/grammar/${id}/update/`, payload);
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`${BASE}/grammar/${id}/delete/`);
    return data;
  },
};

/* ============================================
   Reading (Passage + Questions) — CRUD
   ============================================ */
export const languageReadingAPI = {
  createPassage: async (payload) => {
    const { data } = await api.post(`${BASE}/reading/passages/create/`, payload);
    return data;
  },
  updatePassage: async (id, payload) => {
    const { data } = await api.patch(`${BASE}/reading/passages/${id}/update/`, payload);
    return data;
  },
  deletePassage: async (id) => {
    const { data } = await api.delete(`${BASE}/reading/passages/${id}/delete/`);
    return data;
  },
  getPassage: async (id) => {
    const { data } = await api.get(`${BASE}/reading/passages/${id}/`);
    return data;
  },
  createQuestion: async (passageId, payload) => {
    const { data } = await api.post(
      `${BASE}/reading/passages/${passageId}/questions/create/`,
      payload
    );
    return data;
  },
  updateQuestion: async (id, payload) => {
    const { data } = await api.patch(`${BASE}/reading/questions/${id}/update/`, payload);
    return data;
  },
  deleteQuestion: async (id) => {
    const { data } = await api.delete(`${BASE}/reading/questions/${id}/delete/`);
    return data;
  },
};

/* ============================================
   Listening (Audio + Questions) — CRUD
   ============================================ */
// بيحدد الـ config حسب نوع الـ body + بيدعم progress للرفع
const sendConfig = (body, onProgress) => ({
  ...(body instanceof FormData ? formDataConfig : {}),
  onUploadProgress: onProgress
    ? (e) => onProgress(Math.round((e.loaded * 100) / (e.total || 1)))
    : undefined,
});

export const languageListeningAPI = {
  createAudio: async (formData, onProgress) =>
    (await api.post(`${BASE}/listening/audio/create/`, formData, sendConfig(formData, onProgress))).data,
  updateAudio: async (id, payload, onProgress) => {
    const body = payload instanceof FormData ? payload : payload.audio_file instanceof File ? toFormData(payload) : payload;
    return (await api.patch(`${BASE}/listening/audio/${id}/update/`, body, sendConfig(body, onProgress))).data;
  },
  deleteAudio: async (id) => (await api.delete(`${BASE}/listening/audio/${id}/delete/`)).data,
  createQuestion: async (audioId, payload) =>
    (await api.post(`${BASE}/listening/audio/${audioId}/questions/create/`, payload)).data,
  updateQuestion: async (id, payload) =>
    (await api.patch(`${BASE}/listening/questions/${id}/update/`, payload)).data,
  deleteQuestion: async (id) => (await api.delete(`${BASE}/listening/questions/${id}/delete/`)).data,
};

export const languageSpeakingAPI = {
  // كان بيبعت JSON → الفيديو ماكانش بيوصل. لازم multipart
  createVideo: async (formData, onProgress) =>
    (await api.post(`${BASE}/speaking/videos/create/`, formData, sendConfig(formData, onProgress))).data,
  updateVideo: async (id, payload, onProgress) => {
    const body = payload instanceof FormData ? payload : payload.video_file instanceof File ? toFormData(payload) : payload;
    return (await api.patch(`${BASE}/speaking/videos/${id}/update/`, body, sendConfig(body, onProgress))).data;
  },
  deleteVideo: async (id) => (await api.delete(`${BASE}/speaking/videos/${id}/delete/`)).data,
  createQuestion: async (videoId, payload) =>
    (await api.post(`${BASE}/speaking/videos/${videoId}/questions/create/`, payload)).data,
  updateQuestion: async (id, payload) =>
    (await api.patch(`${BASE}/speaking/questions/${id}/update/`, payload)).data,
  deleteQuestion: async (id) => (await api.delete(`${BASE}/speaking/questions/${id}/delete/`)).data,
};

/* ============================================
   Writing Questions — CRUD
   ============================================ */
export const languageWritingAPI = {
  create: async (payload) => {
    const { data } = await api.post(`${BASE}/writing/questions/create/`, payload);
    return data;
  },
  update: async (id, payload) => {
    const { data } = await api.patch(`${BASE}/writing/questions/${id}/update/`, payload);
    return data;
  },
  delete: async (id) => {
    const { data } = await api.delete(`${BASE}/writing/questions/${id}/delete/`);
    return data;
  },
};

/* ============================================
   AI GENERATION
   ============================================ */
export const languageAIAPI = {
  // Books
  extractBook: async (formData) =>
    (await api.post(`${BASE}/ai/extract-book/upload/`, formData, formDataConfig))
      .data,
  getExtractBookStatus: async (bookId) =>
    (await api.get(`${BASE}/ai/extract-book/${bookId}/status/`)).data,
  listExtractedBooks: async () =>
    (await api.get(`${BASE}/ai/extract-book/`)).data,

  // Media
  extractMedia: async (formData) =>
    (await api.post(`${BASE}/ai/extract-media/upload/`, formData, formDataConfig))
      .data,
  getExtractMediaStatus: async (mediaId) =>
    (await api.get(`${BASE}/ai/extract-media/${mediaId}/status/`)).data,
  listExtractedMedia: async () =>
    (await api.get(`${BASE}/ai/extract-media/`)).data,

  // Generation  (payload: category_id)
  generateSkill: async (data) =>
    (await api.post(`${BASE}/ai/generate-skill/`, data)).data,
  addQuestionsToSkill: async (data) =>
    (await api.post(`${BASE}/ai/add-questions/`, data)).data,
  getJobStatus: async (jobId) =>
    (await api.get(`${BASE}/ai/jobs/${jobId}/status/`)).data,
  // params: { language_id } أو { category_id }
  listJobs: async (params = {}) =>
    (await api.get(`${BASE}/ai/jobs/`, { params })).data,
};


/* ============================================
   SUBSCRIPTION PLANS (أدمن)
   ============================================ */
export const languagePlansAPI = {
  listAll: async (categoryId) =>
    (await api.get(`${BASE}/categories/${categoryId}/subscription/plans/all/`)).data,
  create: async (categoryId, payload) =>
    (await api.post(`${BASE}/categories/${categoryId}/subscription/plans/create/`, payload)).data,
  update: async (planId, payload) =>
    (await api.patch(`${BASE}/subscription/plans/${planId}/update/`, payload)).data,
  deactivate: async (planId) =>
    (await api.delete(`${BASE}/subscription/plans/${planId}/delete/`)).data,
};
// بتجيب كل العناصر (كل الصفحات) — صفحة التفاصيل وصفحات التعديل محتاجينها كلها
export const getAllSkillItems = async (skillId, pageSize = 100) => {
  const first = await getSkillQuestions(skillId, 1, pageSize);
  let items = [...(first.questions || [])];
  const totalPages = first.pagination?.total_pages || 1;
  for (let p = 2; p <= totalPages; p++) {
    const d = await getSkillQuestions(skillId, p, pageSize);
    items = items.concat(d.questions || []);
  }
  return items;
};

export default {
  languagesAPI,
  languageCategoriesAPI,
  languageSkillsAPI,
  languageQuestionsAPI,
  getSkillQuestions,
  languageVocabularyAPI,
  languagePlansAPI ,
  languageGrammarAPI,
  languageReadingAPI,
  languageListeningAPI,
  languageSpeakingAPI,
  languageWritingAPI,
  languageAIAPI,
};